import { useState } from "react";

const faqs = [
  {
    category: "Reaktionszeiten",
    icon: "ri-time-line",
    questions: [
      {
        q: "Wie schnell reagieren Sie bei einem kurzfristigen Einsatz?",
        a: "Bei dringenden Anfragen sind wir in der Regel innerhalb von 24 Stunden einsatzbereit. In vielen Fällen – besonders im Raum Ratingen und Düsseldorf – können wir noch am selben Tag vor Ort sein. Rufen Sie uns einfach an: 02102 7398070.",
      },
      {
        q: "Sind Sie auch an Wochenenden und Feiertagen erreichbar?",
        a: "Unser Büro ist Mo–Fr von 10:00–15:00 Uhr und Sa von 12:00–15:00 Uhr erreichbar. Für Notfälle – insbesondere beim Schlüsseldienst – sind wir 24/7 für Sie da, auch an Sonn- und Feiertagen.",
      },
      {
        q: "Was passiert, wenn ein anderer Dienstleister kurzfristig ausfällt?",
        a: "Genau für solche Situationen sind wir aufgestellt. Mit über 50 Mitarbeitern und einem starken Partnernetzwerk können wir kurzfristig einspringen und stabile Abläufe sicherstellen – ohne lange Vorlaufzeiten oder Engpässe.",
      },
    ],
  },
  {
    category: "Einsatzgebiet",
    icon: "ri-map-pin-line",
    questions: [
      {
        q: "In welchen Städten und Regionen sind Sie tätig?",
        a: "Wir sind NRW-weit tätig, mit Schwerpunkt auf Ratingen, Düsseldorf, Mettmann, Erkrath, Hilden, Langenfeld, Velbert, Heiligenhaus, Wülfrath, Neuss und Krefeld. Sprechen Sie uns an – wir prüfen gerne, ob wir auch Ihren Standort abdecken können.",
      },
      {
        q: "Betreuen Sie auch Objekte außerhalb von Ratingen?",
        a: "Ja, unser Einsatzgebiet erstreckt sich über ganz Nordrhein-Westfalen. Wir betreuen Objekte von Düsseldorf bis ins Ruhrgebiet und darüber hinaus. Für größere Entfernungen sprechen wir die Konditionen individuell ab.",
      },
      {
        q: "Können Sie mehrere Objekte an verschiedenen Standorten gleichzeitig betreuen?",
        a: "Absolut. Wir haben Erfahrung in der gleichzeitigen Betreuung mehrerer Objekte an unterschiedlichen Standorten in NRW. Ein zentraler Ansprechpartner koordiniert dabei alle Einsätze – das spart Ihnen Zeit und Aufwand.",
      },
    ],
  },
  {
    category: "Leistungsumfang",
    icon: "ri-service-line",
    questions: [
      {
        q: "Welche Leistungen bietet IGM Facility Service an?",
        a: "Wir bieten ein vollständiges Leistungspaket für Immobilien: Gebäudereinigung (Unterhalts-, Grund- und Büroreinigung), Hausmeisterservice, Grünpflege, Fensterreinigung, Entrümpelung sowie Renovierung und Sanierung – alles aus einer Hand.",
      },
      {
        q: "Arbeiten Sie sowohl für Privatkunden als auch für Unternehmen?",
        a: "Ja. Wir betreuen Privatkunden, Hausverwaltungen, Wohnungsbaugesellschaften und gewerbliche Immobilieneigentümer. Ob Einfamilienhaus, Mehrfamilienhaus oder Gewerbeimmobilie – wir haben die passende Lösung für jeden Bedarf.",
      },
      {
        q: "Kann ich alle Leistungen über einen einzigen Ansprechpartner buchen?",
        a: "Genau das ist unser Kernversprechen. Sie haben einen festen Ansprechpartner für alle Leistungen – keine Koordination zwischen verschiedenen Firmen, kein Hinterherlaufen. Wir übernehmen die komplette Betreuung Ihrer Objekte.",
      },
      {
        q: "Wie erhalte ich ein Angebot?",
        a: "Kontaktieren Sie uns telefonisch unter 02102 7398070 oder über unser Kontaktformular. Wir vereinbaren einen kostenlosen Vor-Ort-Termin, besprechen Ihre Anforderungen und erstellen Ihnen ein unverbindliches, transparentes Angebot – in der Regel innerhalb von 24 Stunden.",
      },
    ],
  },
];

export default function FaqSection() {
  const [openKey, setOpenKey] = useState<string | null>("0-0");

  const toggle = (key: string) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  return (
    <section className="py-24 md:py-32 bg-white">
      {/* Schema.org FAQ structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.flatMap((cat) =>
              cat.questions.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.a,
                },
              }))
            ),
          }),
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-4 block">FAQ</span>
          <h2
            className="text-4xl md:text-5xl font-bold text-black leading-tight mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Häufig gestellte Fragen
          </h2>
          <p className="text-gray-500 text-base leading-relaxed">
            Antworten auf die wichtigsten Fragen rund um unsere Dienstleistungen in Ratingen und ganz NRW.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-col gap-10">
          {faqs.map((cat, ci) => (
            <div key={ci}>
              {/* Category Label */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100">
                  <i className={`${cat.icon} text-black text-base`}></i>
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{cat.category}</span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>

              {/* Questions */}
              <div className="flex flex-col gap-2">
                {cat.questions.map((faq, qi) => {
                  const key = `${ci}-${qi}`;
                  const isOpen = openKey === key;

                  return (
                    <div
                      key={qi}
                      className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                        isOpen
                          ? "border-gray-900 bg-gray-950"
                          : "border-gray-200 bg-white hover:border-gray-400"
                      }`}
                    >
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                        aria-expanded={isOpen}
                      >
                        <h4
                          className={`text-sm md:text-base font-semibold leading-snug transition-colors ${
                            isOpen ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {faq.q}
                        </h4>
                        <div
                          className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${
                            isOpen
                              ? "bg-white/10 text-white rotate-45"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          <i className="ri-add-line text-base"></i>
                        </div>
                      </button>

                      <div
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <p className="px-6 pb-6 text-sm md:text-base text-gray-300 leading-relaxed">
                          {faq.a}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 md:mt-14 bg-[#F5F5F3] rounded-2xl p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
          <div>
            <p className="text-base font-bold text-black mb-1">Ihre Frage ist nicht dabei?</p>
            <p className="text-sm text-gray-500">Wir antworten in der Regel innerhalb von 24 Stunden.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="tel:+492102739807"
              className="inline-flex items-center justify-center gap-2 bg-black text-white text-sm font-semibold px-6 py-3.5 rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              02102 7398070
            </a>
            <a
              href="#kontakt"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("kontakt");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 text-sm font-semibold px-6 py-3.5 rounded-full hover:border-black hover:text-black transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              Nachricht schreiben
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
